/**
 * Macro #26 · Increment 2 — DEDICATED XYZ VOXEL EDITOR (owner-facing).
 *
 * Full-territory context, ONE active Work Art. Reuses the CERTIFIED owner Draft
 * clients (loadDraft3D / saveDraft3D) + the frozen territory geometry from the
 * Canvas authority. The BACKEND remains the absolute authority for capacity /
 * collision / bounds / Z / revision — this editor only pre-flights with the
 * shared PURE law so feedback is immediate. The 3D canvas itself is lazy-loaded
 * (three lives only in that chunk).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAccount } from "wagmi";

import { loadCanvas, CanvasApiError } from "../../lib/canvas-api";
import { loadDraft3D, saveDraft3D, Territory3DApiError, type Draft3DDoc } from "../../lib/territory3d-api";
import type { TerritoryGeometry } from "../../domain/canvas-model";
import {
  ownedCells,
  footprintBounds,
  validateScene,
  expandWorkArt,
  capacitySummary,
  Z_MAX_FREE,
  type WorkArt,
  type Voxel,
  type Rect,
} from "../../domain/voxel3d";
import { imageToWorkArt, imageDataToColorGrid } from "../../domain/image-to-voxel";
import { LazyVoxelEditorCanvas } from "../../voxel3d/LazyVoxelEditorCanvas";
import { cn } from "../cn";
import { useI18n } from "../../i18n";

const PALETTE = [
  "#f5f7fa", "#c7ccd6", "#8b93a3", "#4b5261", "#232833", "#0a0e14",
  "#ff5a5a", "#ff8a3d", "#f6c343", "#ffe066", "#22c55e", "#10b981",
  "#22d3ee", "#38bdf8", "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7",
  "#ec4899", "#f472b6", "#b45309", "#7c3f16", "#065f46", "#134e4a",
];

interface LocalVoxel { x: number; y: number; z: number; color: string }

function Shell({ children }: { children: React.ReactNode }) {
  return <div data-testid="voxel-editor-route" className="w-full h-full bg-obsidian text-text-primary">{children}</div>;
}
function Msg({ children, testid }: { children: React.ReactNode; testid: string }) {
  return <div data-testid={testid} className="mx-auto mt-24 w-[min(560px,90%)] rounded-lg border border-border-soft bg-panel p-8 text-[13px]">{children}</div>;
}

export function VoxelEditorRoute({ identityId }: { identityId: string | null }) {
  const { t } = useI18n();
  const { address, isConnected } = useAccount();
  const [data, setData] = useState<{ territory: TerritoryGeometry; draft: Draft3DDoc } | null>(null);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const genRef = useRef(0);

  const reload = useCallback(() => {
    if (!identityId || !isConnected || !address) { setLoading(false); return; }
    const myGen = ++genRef.current;
    const controller = new AbortController();
    setLoading(true); setError(null);
    Promise.all([loadCanvas(identityId, controller.signal), loadDraft3D(identityId, controller.signal)])
      .then(([doc, draft]) => {
        if (myGen !== genRef.current) return;
        setData({ territory: doc.territory ?? { parcels: [], bounds: null, composition: {}, primary: null, member_parcel_ids: [] }, draft });
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted || myGen !== genRef.current) return;
        const err = e as CanvasApiError | Territory3DApiError;
        setError({ code: err.code ?? "VOXEL_ERROR", message: err.message ?? t("editor.voxel.failedToLoad") });
        setLoading(false);
      });
    return () => controller.abort();
  }, [identityId, isConnected, address]);

  useEffect(() => { const c = reload(); return c; }, [reload]);

  if (!identityId) return <Shell><Msg testid="voxel-invalid">{t("editor.voxel.invalid")}</Msg></Shell>;
  if (!isConnected || !address) return <Shell><Msg testid="voxel-auth-required">{t("editor.voxel.authRequired")} <a className="text-neon-teal" href="#auth">{t("editor.voxel.signIn")}</a></Msg></Shell>;
  if (loading) return <Shell><Msg testid="voxel-loading">{t("editor.voxel.loading")}</Msg></Shell>;
  if (error) return <Shell><Msg testid="voxel-error">{error.code}: {error.message} <a className="text-neon-teal ml-2" href="#dashboard">{t("editor.voxel.back")}</a></Msg></Shell>;
  if (!data) return <Shell><Msg testid="voxel-empty-state">{t("editor.voxel.empty")}</Msg></Shell>;
  return <VoxelEditor key={data.draft.identity_id} identityId={identityId} territory={data.territory} draft={data.draft} onReload={reload} />;
}

async function fileToColorGrid(file: File) {
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    const w = Math.max(1, Math.min(512, img.naturalWidth));
    const h = Math.max(1, Math.min(512, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    return imageDataToColorGrid(data, w, h);
  } finally {
    URL.revokeObjectURL(url);
  }
}

function VoxelEditor({
  identityId,
  territory,
  draft,
  onReload,
}: {
  identityId: string;
  territory: TerritoryGeometry;
  draft: Draft3DDoc;
  onReload: () => void;
}) {
  const { t } = useI18n();
  const [workArts, setWorkArts] = useState<WorkArt[]>(() => draft.work_arts ?? []);
  const [authority, setAuthority] = useState(draft.authority);
  const [capacityVoxels, setCapacityVoxels] = useState(draft.capacity.capacity_voxels);
  const [activeId, setActiveId] = useState<string | null>(draft.work_arts?.[0]?.work_art_id ?? null);
  const [mode, setMode] = useState<"add" | "remove">("add");
  const [color, setColor] = useState(PALETTE[0]);
  const [depth, setDepth] = useState(4);
  const [saveState, setSaveState] = useState<"saved" | "dirty" | "saving" | "error">("saved");
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resetSignal, setResetSignal] = useState(0);
  const [converting, setConverting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const rects: Rect[] = useMemo(
    () => territory.parcels.map((p) => ({ x: p.x, y: p.y, w: p.w, h: p.h })),
    [territory.parcels],
  );
  const cells = useMemo(() => ownedCells(rects), [rects]);
  const footprint = useMemo(() => footprintBounds(rects), [rects]);

  const preflight = useMemo(() => validateScene(workArts, cells), [workArts, cells]);
  const usedLive = preflight.ok ? preflight.used : workArts.reduce((n, wa) => n + wa.voxels.length, 0);
  const cap = capacitySummary(usedLive, capacityVoxels);

  const active = useMemo(() => workArts.find((w) => w.work_art_id === activeId) ?? null, [workArts, activeId]);

  // world-occupancy across ALL work arts (for collision pre-flight)
  const occupied = useMemo(() => {
    const set = new Set<string>();
    for (const wa of workArts) {
      try {
        for (const v of expandWorkArt(wa)) set.add(`${v.x},${v.y},${v.z}`);
      } catch { /* skip invalid */ }
    }
    return set;
  }, [workArts]);

  const markDirty = () => { setSaveState("dirty"); setSaveErr(null); };

  const flash = (m: string) => { setNotice(m); window.setTimeout(() => setNotice((n) => (n === m ? null : n)), 2200); };

  // --- Undo / Redo history (snapshots of the whole scene) ---
  const undoRef = useRef<WorkArt[][]>([]);
  const redoRef = useRef<WorkArt[][]>([]);
  const [, setHistTick] = useState(0);
  const canUndo = undoRef.current.length > 0;
  const canRedo = redoRef.current.length > 0;

  const commit = useCallback((fn: (prev: WorkArt[]) => WorkArt[]) => {
    setWorkArts((prev) => {
      undoRef.current.push(prev);
      if (undoRef.current.length > 80) undoRef.current.shift();
      redoRef.current = [];
      return fn(prev);
    });
    setHistTick((t) => t + 1);
    markDirty();
  }, []);
  const undo = useCallback(() => {
    setWorkArts((cur) => {
      const prev = undoRef.current.pop();
      if (prev === undefined) return cur;
      redoRef.current.push(cur);
      return prev;
    });
    setHistTick((t) => t + 1);
    markDirty();
  }, []);
  const redo = useCallback(() => {
    setWorkArts((cur) => {
      const next = redoRef.current.pop();
      if (next === undefined) return cur;
      undoRef.current.push(cur);
      return next;
    });
    setHistTick((t) => t + 1);
    markDirty();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  // "Define base" flow: the user drags a rectangle on the ground grid to size a
  // new piece (position + size), bounded by the remaining voxel budget and the
  // owned footprint, with no overlap between bases.
  const [defining, setDefining] = useState(false);
  const [defineDraft, setDefineDraft] = useState<{ w: number; h: number; valid: boolean } | null>(null);

  const startDefine = () => {
    if (capacityVoxels <= 0) { flash("No free 3D capacity for this territory"); return; }
    setDefineDraft(null);
    setDefining(true);
  };
  const cancelDefine = () => { setDefining(false); setDefineDraft(null); };

  const setActiveVoxels = (updater: (voxels: Voxel[]) => Voxel[]) => {
    if (!active) return;
    commit((prev) => prev.map((w) => (w.work_art_id === active.work_art_id ? { ...w, voxels: updater(w.voxels) } : w)));
  };

  const onAddVoxel = useCallback(
    (lx: number, ly: number, lz: number) => {
      if (!active) { flash(t("editor.voxel.flashCreateFirst")); return; }
      if ((active.rotation ?? 0) !== 0) { flash("Reset rotation to 0° to edit voxels"); return; }
      const av = active.allowed_volume;
      const maxZ = Math.min(av.d, Z_MAX_FREE);
      if (!(lx >= 0 && lx < av.w && ly >= 0 && ly < av.h && lz >= 0 && lz < maxZ)) return;
      const wx = active.placement.x + lx;
      const wy = active.placement.y + ly;
      const wz = active.placement.z + lz;
      if (!cells.has(`${wx},${wy}`)) { flash(t("editor.voxel.flashOutside")); return; }
      if (occupied.has(`${wx},${wy},${wz}`)) return;
      if (cap.used_voxels >= capacityVoxels) {
        flash(capacityVoxels === 0
          ? "This territory has no free 3D capacity (see the note above)"
          : "At capacity — remove voxels or free capacity");
        return;
      }
      setActiveVoxels((vs) => [...vs, [lx, ly, lz, color] as Voxel]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active, cells, occupied, cap.used_voxels, capacityVoxels, color],
  );

  const onRemoveVoxel = useCallback(
    (index: number) => {
      setActiveVoxels((vs) => vs.filter((_, i) => i !== index));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active],
  );

  const rotateActive = () => {
    if (!active) return;
    const next = (((active.rotation ?? 0) + 90) % 360) as WorkArt["rotation"];
    commit((prev) => prev.map((w) => (w.work_art_id === active.work_art_id ? { ...w, rotation: next } : w)));
  };

  const deleteActive = () => {
    if (!active) return;
    const id = active.work_art_id;
    commit((prev) => prev.filter((w) => w.work_art_id !== id));
    setActiveId((prev) => (prev === id ? null : prev));
  };

  const clearActive = () => {
    if (!active || active.voxels.length === 0) return;
    const id = active.work_art_id;
    commit((prev) => prev.map((w) => (w.work_art_id === id ? { ...w, voxels: [] } : w)));
    flash(t("editor.voxel.flashCleared"));
  };

  const onPickImage = async (file: File | null) => {
    if (!file || !footprint) return;
    setConverting(true);
    try {
      const grid = await fileToColorGrid(file);
      if (!grid) { flash(t("editor.voxel.flashNoImage")); return; }
      const result = imageToWorkArt(grid, {
        depth,
        maxW: footprint.w,
        maxH: footprint.h,
        capacity: Math.max(0, capacityVoxels - usedLive),
        placement: { x: footprint.x, y: footprint.y, z: 0 },
        workArtId: `img-${Date.now().toString(36)}`,
      });
      commit((prev) => [...prev, result.workArt]);
      setActiveId(result.workArt.work_art_id);
      flash(
        result.truncated
          ? "Converted (trimmed to fit capacity). Refine it voxel by voxel."
          : "Converted. Refine the result voxel by voxel.",
      );
    } finally {
      setConverting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doSave = async () => {
    setSaveState("saving"); setSaveErr(null);
    try {
      const saved = await saveDraft3D(identityId, workArts, authority);
      setWorkArts(saved.work_arts);
      undoRef.current = []; redoRef.current = []; setHistTick((t) => t + 1);
      setAuthority(saved.authority);
      setCapacityVoxels(saved.capacity.capacity_voxels);
      setSaveState("saved");
      flash("Saved — publish it from the Canvas (← top-left)");
    } catch (e) {
      const err = e as Territory3DApiError;
      setSaveState("error");
      setSaveErr(err.code === "TERRITORY_CHANGED" ? "Territory changed — reload editor" : `${err.code}: ${err.message}`);
    }
  };

  // other work arts → active-local frame for translucent context rendering
  const otherVoxels = useMemo<LocalVoxel[]>(() => {
    if (!active) return [];
    const ox = active.placement.x;
    const oy = active.placement.y;
    const out: LocalVoxel[] = [];
    for (const wa of workArts) {
      if (wa.work_art_id === active.work_art_id) continue;
      try {
        for (const v of expandWorkArt(wa)) out.push({ x: v.x - ox, y: v.y - oy, z: v.z, color: v.color });
      } catch { /* skip */ }
    }
    return out;
  }, [workArts, active]);

  const footW = active?.allowed_volume.w ?? footprint?.w ?? 10;
  const footH = active?.allowed_volume.h ?? footprint?.h ?? 10;
  const needsFirstBlock = !!active && active.voxels.length === 0;

  // Bases of all pieces in FOOTPRINT-local cells (for the define overlay + the
  // budget rule: Σ base areas must fit the voxel capacity).
  const existingBases = useMemo(() => {
    if (!footprint) return [] as { x: number; y: number; w: number; h: number }[];
    return workArts.map((wa) => ({
      x: wa.placement.x - footprint.x,
      y: wa.placement.y - footprint.y,
      w: wa.allowed_volume.w,
      h: wa.allowed_volume.h,
    }));
  }, [workArts, footprint]);
  const usedBaseCells = useMemo(() => existingBases.reduce((n, b) => n + b.w * b.h, 0), [existingBases]);
  const remainingBaseCells = Math.max(0, capacityVoxels - usedBaseCells);

  const onDefineBase = useCallback((lx: number, ly: number, w: number, h: number) => {
    if (!footprint) return;
    if (!(lx >= 0 && ly >= 0 && lx + w <= footprint.w && ly + h <= footprint.h)) {
      flash(t("editor.voxel.flashBaseInside")); return;
    }
    const wa: WorkArt = {
      work_art_id: `wa-${Date.now().toString(36)}`,
      allowed_volume: { w, h, d: Z_MAX_FREE },
      placement: { x: footprint.x + lx, y: footprint.y + ly, z: 0 },
      rotation: 0,
      voxels: [],
    };
    commit((prev) => [...prev, wa]);
    setActiveId(wa.work_art_id);
    setDefining(false);
    setDefineDraft(null);
    flash(`Base ${w}×${h} created — left-click the floor to place blocks`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [footprint]);

  if (!footprint) {
    return <Shell><Msg testid="voxel-no-footprint">{t("editor.voxel.noFootprint")}</Msg></Shell>;
  }

  return (
    <div data-testid="voxel-editor" className="w-full h-full flex flex-col bg-obsidian text-text-primary select-none">
      {/* TOP BAR */}
      <div className="flex items-center gap-2 h-12 px-3 border-b border-border-soft bg-panel shrink-0 overflow-x-auto no-scrollbar">
        <a data-testid="voxel-back" href={`#canvas/${identityId}`} className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-secondary hover:text-text-primary no-underline">← {t("editor.voxel.canvas")}</a>
        <div className="mx-1 h-5 w-px bg-border-soft" />
        <button data-testid="voxel-add-mode-btn" data-active={!defining && mode === "add"} onClick={() => { cancelDefine(); setMode("add"); }}
          className={cn("chip", !defining && mode === "add" && "border-border-accent text-neon-teal")}>{t("editor.voxel.add")}</button>
        <button data-testid="voxel-remove-mode-btn" data-active={!defining && mode === "remove"} onClick={() => { cancelDefine(); setMode("remove"); }}
          className={cn("chip", !defining && mode === "remove" && "border-red-300/60 text-red-200")}>{t("editor.voxel.remove")}</button>
        {defining && (
          <button data-testid="voxel-define-cancel" onClick={cancelDefine}
            className="chip border-amber-300/60 text-amber-200">{t("editor.voxel.cancelBase")}</button>
        )}
        <div className="mx-1 h-5 w-px bg-border-soft" />
        {/* current color — full palette + custom picker live in the sidebar */}
        <span data-testid="voxel-current-color" title={t("editor.voxel.currentColorTitle")}
          className="h-6 w-6 rounded-sm border border-neon-teal ring-1 ring-neon-teal shrink-0" style={{ background: color }} />
        <div className="mx-1 h-5 w-px bg-border-soft" />
        {/* history + tools */}
        <button data-testid="voxel-undo-btn" onClick={undo} disabled={!canUndo} title={t("editor.voxel.undoTitle")}
          className="chip disabled:opacity-40 disabled:cursor-not-allowed">↶ {t("editor.voxel.undo")}</button>
        <button data-testid="voxel-redo-btn" onClick={redo} disabled={!canRedo} title={t("editor.voxel.redoTitle")}
          className="chip disabled:opacity-40 disabled:cursor-not-allowed">↷ {t("editor.voxel.redo")}</button>
        <button data-testid="voxel-clear-btn" onClick={clearActive} disabled={!active || active.voxels.length === 0} title={t("editor.voxel.clearTitle")}
          className="chip border-red-300/40 text-red-200 disabled:opacity-40 disabled:cursor-not-allowed">{t("editor.voxel.clear")}</button>
        <div className="mx-1 h-5 w-px bg-border-soft" />
        <button data-testid="voxel-reset-view-btn" onClick={() => setResetSignal((s) => s + 1)} className="chip">{t("editor.voxel.resetView")}</button>
        <div className="ml-auto flex items-center gap-2 shrink-0 pl-2">
          <VoxelCapacityBadge used={cap.used_voxels} capacity={capacityVoxels} over={cap.over_capacity} />
          <button data-testid="voxel-save-btn" onClick={() => void doSave()} disabled={saveState === "saving" || cap.over_capacity}
            title={cap.over_capacity ? t("editor.voxel.saveOverTitle") : t("editor.voxel.saveTitle")}
            className="inline-flex items-center h-8 px-4 rounded-md border border-neon-teal bg-transparent text-neon-teal font-mono text-[11px] uppercase tracking-[0.16em] hover:bg-neon-teal-soft disabled:opacity-40 disabled:cursor-not-allowed">
            {saveState === "saving" ? t("editor.voxel.saving") : saveState === "saved" ? t("editor.voxel.saved") : t("editor.voxel.save")}
          </button>
          <a data-testid="voxel-publish-link" href={`#canvas/${identityId}`}
            title={t("editor.voxel.publishTitle")}
            className="inline-flex items-center h-8 px-3 rounded-md border border-neon-teal bg-neon-teal-soft text-neon-teal font-mono text-[11px] uppercase tracking-[0.14em] hover:bg-neon-teal/20 no-underline whitespace-nowrap">
            {t("editor.voxel.publishLink")}
          </a>
        </div>
      </div>

      {saveErr && (
        <div data-testid="voxel-save-error" className="mx-3 mt-2 rounded-md border border-state-danger/50 bg-state-danger/10 px-3 py-2 text-[11px] font-mono text-state-danger flex items-center justify-between gap-3 shrink-0">
          <span>{saveErr}</span>
          {saveErr.includes("reload") && <button data-testid="voxel-reload" onClick={onReload} className="chip h-7">{t("editor.voxel.reload")}</button>}
        </div>
      )}

      <div className="flex-1 flex min-h-0">
        {/* LEFT — work arts + converter */}
        <div data-testid="voxel-sidebar" className="w-56 shrink-0 border-r border-border-soft bg-panel overflow-y-auto p-3 space-y-3">
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.voxel.color")}</div>
          <div className="flex items-center gap-2">
            <input data-testid="voxel-color-custom" type="color" value={/^#[0-9a-fA-F]{6}$/.test(color) ? color : "#22d3ee"}
              onChange={(e) => setColor(e.target.value)} title={t("editor.voxel.customColorTitle")} className="h-7 w-10 bg-transparent cursor-pointer p-0 border-0" />
            <span className="text-[10px] font-mono text-text-secondary uppercase">{color}</span>
          </div>
          <div className="grid grid-cols-8 gap-1">
            {PALETTE.map((c) => (
              <button key={c} data-testid={`voxel-color-${c}`} onClick={() => setColor(c)} title={c}
                className={cn("h-5 w-full rounded-sm border", color.toLowerCase() === c.toLowerCase() ? "border-neon-teal ring-1 ring-neon-teal" : "border-border-soft")}
                style={{ background: c }} />
            ))}
          </div>
          <div className="h-px bg-border-soft" />
          <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.voxel.workArts")}</div>
          <button data-testid="voxel-new-workart-btn" data-active={defining} onClick={startDefine}
            className={cn("chip w-full", defining && "border-neon-teal text-neon-teal")}>{t("editor.voxel.newBase")}</button>
          <div className="text-[10px] font-mono text-text-muted">{t("editor.voxel.budgetLabel")} <span className="text-text-secondary">{remainingBaseCells.toLocaleString()}</span> / {capacityVoxels.toLocaleString()} {t("editor.voxel.cellsUnit")}</div>
          <div className="space-y-1">
            {workArts.length === 0 && <div className="text-[11px] text-text-muted font-mono">{t("editor.voxel.noWorkArts")}</div>}
            {workArts.map((w) => (
              <button key={w.work_art_id} data-testid={`voxel-workart-${w.work_art_id}`} data-active={w.work_art_id === activeId}
                onClick={() => setActiveId(w.work_art_id)}
                className={cn("w-full text-left px-2 py-1.5 rounded-sm border text-[11px] font-mono flex items-center justify-between gap-2",
                  w.work_art_id === activeId ? "border-border-accent text-neon-teal bg-neon-teal-soft/30" : "border-border-soft text-text-secondary hover:text-text-primary")}>
                <span className="truncate">{w.work_art_id}</span>
                <span className="text-text-muted">{w.voxels.length}</span>
              </button>
            ))}
          </div>

          {active && (
            <div className="pt-1 space-y-2 border-t border-border-soft">
              <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.voxel.activePiece")}</div>
              <div className="flex items-center gap-1">
                <button data-testid="voxel-rotate-btn" onClick={rotateActive} className="chip flex-1">{t("editor.voxel.rotate")} {active.rotation ?? 0}°</button>
                <button data-testid="voxel-delete-workart-btn" onClick={deleteActive} className="chip-xs border-red-300/50 text-red-200">✕</button>
              </div>
              {(active.rotation ?? 0) !== 0 && (
                <div data-testid="voxel-rotation-hint" className="text-[10px] font-mono text-neon-teal">{t("editor.voxel.rotationHint")}</div>
              )}
            </div>
          )}

          <div className="pt-1 space-y-2 border-t border-border-soft">
            <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted">{t("editor.voxel.imageToVoxel")}</div>
            <div className="flex items-center gap-2">
              <label className="text-[10px] font-mono text-text-muted">{t("editor.voxel.depth")}</label>
              <input data-testid="voxel-depth-input" type="range" min={1} max={Z_MAX_FREE} value={depth}
                onChange={(e) => setDepth(Number(e.target.value))} className="flex-1" />
              <span className="text-[10px] font-mono text-text-secondary w-6 text-right">{depth}</span>
            </div>
            <input ref={fileRef} data-testid="voxel-image-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif"
              className="hidden" onChange={(e) => void onPickImage(e.target.files?.[0] ?? null)} />
            <button data-testid="voxel-image-convert-btn" disabled={converting} onClick={() => fileRef.current?.click()}
              className="chip w-full disabled:opacity-40">{converting ? t("editor.voxel.converting") : t("editor.voxel.chooseImage")}</button>
            <p className="text-[10px] leading-snug text-text-muted">{t("editor.voxel.conversionHelp")}</p>
          </div>
        </div>

        {/* CENTER — 3D editor canvas */}
        <div data-testid="voxel-stage" className="flex-1 relative min-h-0 bg-[#05070d]" onContextMenu={(e) => e.preventDefault()}>
          <LazyVoxelEditorCanvas
            activeVoxels={active?.voxels ?? []}
            otherVoxels={otherVoxels}
            footprintW={defining ? footprint.w : footW}
            footprintH={defining ? footprint.h : footH}
            mode={defining ? "define" : mode}
            onAddVoxel={onAddVoxel}
            onRemoveVoxel={onRemoveVoxel}
            resetSignal={resetSignal}
            highlightGround={needsFirstBlock && capacityVoxels > 0 && !defining}
            onDefineBase={onDefineBase}
            onDefineDraftChange={setDefineDraft}
            existingBases={existingBases}
            defineMaxCells={remainingBaseCells}
            loading={<div data-testid="voxel-canvas-loading" className="absolute inset-0 grid place-items-center text-[12px] font-mono uppercase tracking-[0.16em] text-neon-teal">{t("editor.voxel.loading")}</div>}
            fallback={<div data-testid="voxel-canvas-fallback" className="absolute inset-0 grid place-items-center p-6 text-center text-[13px] text-text-secondary">{t("editor.voxel.fallback")}</div>}
          />
          {notice && (
            <div data-testid="voxel-notice" className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full border border-neon-teal/60 bg-panel px-4 py-2 text-[11px] font-mono uppercase tracking-[0.12em] text-neon-teal shadow-lg">
              {notice}
            </div>
          )}
          {defining && (
            <div data-testid="voxel-define-banner" className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 w-[min(560px,90%)] rounded-md border border-neon-teal/50 bg-panel/90 px-4 py-2.5 text-[11px] leading-snug font-mono text-text-secondary">
              <div className="uppercase tracking-[0.16em] text-neon-teal mb-0.5">{t("editor.voxel.defineTitle")}</div>
              {t("editor.voxel.defineBody")}
              <span className="ml-1">
                {defineDraft
                  ? <>{t("editor.voxel.sizeLabel")} <b className={defineDraft.valid ? "text-neon-teal" : "text-red-300"}>{defineDraft.w}×{defineDraft.h}</b> = <b className={defineDraft.valid ? "text-neon-teal" : "text-red-300"}>{(defineDraft.w * defineDraft.h).toLocaleString()}</b> / {remainingBaseCells.toLocaleString()} {t("editor.voxel.cellsUnit")} {defineDraft.valid ? "" : t("editor.voxel.tooBig")}</>
                  : <>{t("editor.voxel.budgetLabel")} <b className="text-neon-teal">{remainingBaseCells.toLocaleString()}</b> {t("editor.voxel.cellsUnit")}</>}
              </span>
            </div>
          )}
          {capacityVoxels === 0 && (
            <div data-testid="voxel-no-capacity" className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 w-[min(540px,90%)] rounded-md border border-amber-400/50 bg-amber-500/10 px-4 py-2.5 text-[11px] leading-snug font-mono text-amber-200">
              <div className="uppercase tracking-[0.16em] text-amber-300 mb-0.5">{t("editor.voxel.noCapacityTitle")}</div>
              {t("editor.voxel.noCapacityBody")}
            </div>
          )}
          {!active && !defining && capacityVoxels > 0 && (
            <div className="pointer-events-none absolute top-4 left-1/2 -translate-x-1/2 rounded-full border border-border-soft bg-panel/90 px-4 py-2 text-[11px] font-mono text-text-secondary">
              {t("editor.voxel.hintNewBase")}
            </div>
          )}
          <VoxelHelpLegend needsFirstBlock={needsFirstBlock} defining={defining} />
        </div>
      </div>
    </div>
  );
}

function MouseGlyph({ btn }: { btn: "left" | "right" | "wheel" }) {
  return (
    <svg width="13" height="17" viewBox="0 0 13 17" className="shrink-0" aria-hidden="true">
      <rect x="0.6" y="0.6" width="11.8" height="15.8" rx="5.9" fill="#0b0f16" stroke="currentColor" strokeWidth="1" />
      <line x1="6.5" y1="1" x2="6.5" y2="6.5" stroke="currentColor" strokeWidth="0.9" />
      <line x1="0.9" y1="6.5" x2="12.1" y2="6.5" stroke="currentColor" strokeWidth="0.7" opacity="0.5" />
      {btn === "left" && <path d="M1.1 6.3 V4.4 A5.4 5.4 0 0 1 6.1 1.05 V6.3 Z" fill="currentColor" />}
      {btn === "right" && <path d="M6.9 1.05 A5.4 5.4 0 0 1 11.9 4.4 V6.3 H6.9 Z" fill="currentColor" />}
      {btn === "wheel" && <rect x="5.1" y="2.5" width="2.8" height="4.3" rx="1.4" fill="currentColor" />}
    </svg>
  );
}

function LegendMouseRow({ btn, k, v }: { btn: "left" | "right" | "wheel"; k: string; v: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-text-primary text-[10px] whitespace-nowrap">
        <span className="text-neon-teal"><MouseGlyph btn={btn} /></span>
        {k}
      </span>
      <span className="text-text-muted text-right">{v}</span>
    </div>
  );
}

/**
 * Always-on help overlay (bottom-LEFT of the 3D stage): a short "how to build"
 * guide + the mouse controls with small mouse icons. Collapsible via a "?" pill.
 * Purely informational — the wrapper is pointer-events-none so it never blocks
 * drawing on the canvas underneath; only the collapse/expand buttons are clickable.
 */
function VoxelHelpLegend({ needsFirstBlock, defining }: { needsFirstBlock: boolean; defining: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(true);

  if (!open) {
    return (
      <button data-testid="voxel-help-toggle" onClick={() => setOpen(true)} title={t("editor.voxel.showControls")}
        className="pointer-events-auto absolute bottom-3 left-3 z-10 h-8 w-8 rounded-full border border-neon-teal/60 bg-panel/85 backdrop-blur-sm text-neon-teal font-mono text-[13px] shadow-lg hover:bg-neon-teal-soft">?</button>
    );
  }

  return (
    <div data-testid="voxel-help-legend"
      className="pointer-events-none absolute bottom-3 left-3 z-10 w-[248px] max-w-[74%] rounded-md border border-border-soft bg-panel/85 backdrop-blur-sm p-3 shadow-lg">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-neon-teal">{t("editor.voxel.howToBuild")}</div>
        <button data-testid="voxel-help-collapse" onClick={() => setOpen(false)} title={t("editor.voxel.hide")}
          className="pointer-events-auto -mr-1 -mt-0.5 h-5 w-5 flex items-center justify-center rounded-sm border border-border-soft text-text-muted hover:text-text-primary text-[12px] leading-none">–</button>
      </div>
      <ol data-testid="voxel-help-steps" className="space-y-1 text-[10px] leading-snug text-text-secondary font-mono">
        <li className={defining ? "text-neon-teal" : undefined}>
          <span className="text-neon-teal">1.</span> {t("editor.voxel.step1")}
        </li>
        <li className={needsFirstBlock && !defining ? "text-neon-teal" : undefined}>
          <span className="text-neon-teal">2.</span> {t("editor.voxel.step2")}
        </li>
        <li><span className="text-neon-teal">3.</span> {t("editor.voxel.step3")}</li>
      </ol>
      <div className="my-2 h-px bg-border-soft" />
      <div className="text-[9px] font-mono uppercase tracking-[0.2em] text-text-muted mb-1.5">{t("editor.voxel.mouse")}</div>
      <div data-testid="voxel-help-mouse" className="space-y-1.5 text-[10px] font-mono">
        <LegendMouseRow btn="left" k={t("editor.voxel.leftClick")} v={defining ? t("editor.voxel.drawBase") : t("editor.voxel.placeBlock")} />
        <LegendMouseRow btn="right" k={t("editor.voxel.rightClick")} v={t("editor.voxel.removeBlock")} />
        <LegendMouseRow btn="left" k={t("editor.voxel.ctrlLeft")} v={t("editor.voxel.orbit")} />
        <LegendMouseRow btn="right" k={t("editor.voxel.ctrlRight")} v={t("editor.voxel.pan")} />
        <LegendMouseRow btn="wheel" k={t("editor.voxel.scroll")} v={t("editor.voxel.zoomInOut")} />
      </div>
    </div>
  );
}

function VoxelCapacityBadge({ used, capacity, over }: { used: number; capacity: number; over: boolean }) {
  const { t } = useI18n();
  const pct = capacity > 0 ? Math.min(100, (used / capacity) * 100) : 0;
  return (
    <div data-testid="voxel-capacity-indicator" data-used={used} data-capacity={capacity} data-over={over ? "true" : "false"}
      className="flex flex-col gap-0.5 min-w-[150px]">
      <div className="flex items-center justify-between text-[9px] font-mono uppercase tracking-[0.14em]">
        <span className="text-text-muted">{t("editor.voxel.capacity3d")}</span>
        <span className={cn(over ? "text-state-danger" : "text-text-secondary")}>
          {used.toLocaleString()} / {capacity.toLocaleString()}
        </span>
      </div>
      <div className="h-1.5 w-full rounded-full bg-obsidian border border-border-soft overflow-hidden">
        <div className={cn("h-full rounded-full", over ? "bg-state-danger" : "bg-neon-teal")} style={{ width: `${pct}%` }} />
      </div>
      {over && <div data-testid="voxel-over-capacity" className="text-[9px] font-mono text-state-danger">{t("editor.voxel.overCapacity")}</div>}
    </div>
  );
}
